import React, { useEffect, useState } from "react";
import Slider from "@mui/material/Slider";
import { SLIDER_MAX, formatRupees, rangeFromSlider, sliderRange } from "../../../components/products/InventoryPriceFilters";
import { priceLabel } from "./productListFilters";

type Band = { key: string; label: string; short: string; value: Record<string, any> };

/** Quick bands (design P03 / pane). Bounds inclusive, like the slider. */
export const PRICE_BANDS: Band[] = [
  { key: "u25", label: "Under ₹25k", short: "< ₹25k", value: { preset: "CUSTOM", minSellPrice: "", maxSellPrice: "25000", maxSellPriceInclusive: false } },
  { key: "25-50", label: "₹25k – 50k", short: "₹25–50k", value: { preset: "CUSTOM", minSellPrice: "25000", maxSellPrice: "50000" } },
  { key: "50-1l", label: "₹50k – 1L", short: "₹50k–1L", value: { preset: "CUSTOM", minSellPrice: "50000", maxSellPrice: "100000" } },
  { key: "o1l", label: "Above ₹1L", short: "> ₹1L", value: { preset: "CUSTOM", minSellPrice: "100000", maxSellPrice: "" } },
  { key: "none", label: "No price yet", short: "No price", value: { preset: "UNPRICED", priceStatus: "UNPRICED" } },
];

const same = (a: unknown, b: unknown) => String(a || "") === String(b || "");

export function activeBand(value: Record<string, any> | undefined): string {
  const v = value || {};
  if (v.preset === "UNPRICED") return "none";
  const hit = PRICE_BANDS.find(
    (band) => band.key !== "none" && same(v.minSellPrice, band.value.minSellPrice) && same(v.maxSellPrice, band.value.maxSellPrice)
  );
  return hit && (v.minSellPrice || v.maxSellPrice) ? hit.key : "";
}

/**
 * Selling price filter: quick bands, a two-handle range and "include pieces with no price".
 * Same request params as before (min/max sell price, priceStatus, includeUnpriced).
 */
const PriceFilterBlock = ({
  value = {},
  onChange,
  compact,
}: {
  value?: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  compact?: boolean;
}) => {
  const [slide, setSlide] = useState<[number, number]>(sliderRange(value));
  useEffect(() => {
    setSlide(sliderRange(value));
  }, [value.minSellPrice, value.maxSellPrice, value.preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const band = activeBand(value);
  const unpricedOnly = value.preset === "UNPRICED";
  const ranged = Boolean(value.minSellPrice || value.maxSellPrice);
  const label = (n: number, top: boolean) => (top && n >= SLIDER_MAX ? `${formatRupees(SLIDER_MAX)}+` : formatRupees(n));
  return (
    <div className="pl-price-filter">
      <div className="pl-chips is-wrap" role="group" aria-label="Price bands">
        {PRICE_BANDS.map((b) => (
          <button
            key={b.key}
            type="button"
            aria-pressed={band === b.key}
            className={`pl-chip${band === b.key ? " is-on" : ""}`}
            onClick={() =>
              onChange(
                band === b.key
                  ? { preset: "ALL" }
                  : b.key === "none"
                    ? b.value
                    : { ...b.value, includeUnpriced: value.includeUnpriced === true }
              )
            }
          >
            {compact ? b.short : b.label}
          </button>
        ))}
      </div>
      {!unpricedOnly ? (
        <div className="pl-range">
          <Slider
            value={slide}
            min={0}
            max={SLIDER_MAX}
            step={1000}
            disableSwap
            size="small"
            getAriaLabel={(i) => (i === 0 ? "Lowest price" : "Highest price")}
            getAriaValueText={(n, i) => label(n, i === 1)}
            onChange={(_, next) => setSlide(next as [number, number])}
            onChangeCommitted={(_, next) => onChange(rangeFromSlider(next as [number, number], value.includeUnpriced === true))}
          />
        </div>
      ) : null}
      {ranged ? (
        <div className="pl-fgrp__row">
          <span className="pl-grow">Include pieces with no price</span>
          <button
            type="button"
            role="switch"
            aria-checked={value.includeUnpriced === true}
            aria-label="Include pieces with no price"
            className={`pl-switch${value.includeUnpriced === true ? " is-on" : ""}`}
            onClick={() => onChange({ ...value, includeUnpriced: value.includeUnpriced !== true })}
          >
            <span className="pl-switch__knob" />
          </button>
        </div>
      ) : null}
    </div>
  );
};

export const priceHint = (value: Record<string, any> | undefined) => priceLabel(value) || "Any price";

export default PriceFilterBlock;
